/* eslint-disable max-statements */

import { Button, Group, LoadingOverlay } from '@mantine/core';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { CustomsStateNotificationType, TransitGroup, TransitsCriteria, TransitStatus } from '@e-origin/shared';

import { ListFilterChangeEvent, ListFilterDropdown, ListFilterDropdownElements, SearchBox, Table } from '@components';
import { ContentLayout } from '@features/layout';
import { getInitialCriteria, useTransits } from '@hooks/transit';
import { removeUndefined, STORAGE_KEYS } from '@utils';
import { setStoredCriteria } from '../../../criteria.caching';
import { transitModalManager } from './transitModalManager';
import { TransitType } from './TransitType';
import { useTransitsTableColumns } from './useTransitsTableColumns';

export const TransitList = () => {
  const criteria = getInitialCriteria();
  const storeCriteria = () => {
    setStoredCriteria(STORAGE_KEYS.COOKIES.LIST_TRANSITS_CRITERIA, { filters, sorting, pagination });
  };

  const [filters, setFilters] = useState<TransitsCriteria['filters']>(criteria.filters);
  const [sorting, setSorting] = useState<TransitsCriteria['sorting']>(criteria.sorting);
  const [pagination, setPagination] = useState<TransitsCriteria['pagination']>(criteria.pagination);

  const { data: { list: transitList, total: totalTransits } = {}, isLoading } = useTransits({ filters, sorting, pagination });

  const [transitView, setTransitView] = useState(criteria.filters.group);

  const statusesOptions = useMemo<ListFilterDropdownElements>(() => {
    return {
      [TransitStatus.NOT_SENT as string]: {
        label: 'Not sent',
        checked: filters.status?.includes(TransitStatus.NOT_SENT) ?? false,
      },
      [TransitStatus.SENDING as string]: {
        label: 'Sending',
        checked: filters.status?.includes(TransitStatus.SENDING) ?? false,
      },
      [TransitStatus.SENT as string]: {
        label: 'Sent',
        checked: filters.status?.includes(TransitStatus.SENT) ?? false,
      },
      [TransitStatus.ACCEPTED as string]: {
        label: 'Accepted',
        checked: filters.status?.includes(TransitStatus.ACCEPTED) ?? false,
      },
      [TransitStatus.REJECTED as string]: {
        label: 'Rejected',
        checked: filters.status?.includes(TransitStatus.REJECTED) ?? false,
      },
      [TransitStatus.UNLOAD_PERMISSION as string]: {
        label: 'Unload permission',
        checked: filters.status?.includes(TransitStatus.UNLOAD_PERMISSION) ?? false,
      },
      [TransitStatus.REMARKS_SENT as string]: {
        label: 'Remarks sent',
        checked: filters.status?.includes(TransitStatus.REMARKS_SENT) ?? false,
      },
      [TransitStatus.IN_CONTROL as string]: {
        label: 'In control',
        checked: filters.status?.includes(TransitStatus.IN_CONTROL) ?? false,
      },
      [TransitStatus.CORR_REFUSED_WAITING as string]: {
        label: 'In control corr refused',
        checked: filters.status?.includes(TransitStatus.CORR_REFUSED_WAITING) ?? false,
      },
      [TransitStatus.CORR_OK_WAITING as string]: {
        label: 'In control corr ok',
        checked: filters.status?.includes(TransitStatus.CORR_OK_WAITING) ?? false,
      },
      [TransitStatus.REQUESTED_NON_ARRIVED as string]: {
        label: 'Requested non arrival',
        checked: filters.status?.includes(TransitStatus.REQUESTED_NON_ARRIVED) ?? false,
      },
      [TransitStatus.NOT_RELEASED as string]: {
        label: 'Not released',
        checked: filters.status?.includes(TransitStatus.NOT_RELEASED) ?? false,
      },
      [TransitStatus.RELEASED as string]: {
        label: 'Released',
        checked: filters.status?.includes(TransitStatus.RELEASED) ?? false,
      },
      [TransitStatus.FINISHED as string]: {
        label: 'Transit finished',
        checked: filters.status?.includes(TransitStatus.FINISHED) ?? false,
      },
      [TransitStatus.INVALIDATED as string]: {
        label: 'Invalidated',
        checked: filters.status?.includes(TransitStatus.INVALIDATED) ?? false,
      },
      [TransitStatus.GUARANTEE_NOT_VALID as string]: {
        label: 'Guarantee not valid',
        checked: filters.status?.includes(TransitStatus.GUARANTEE_NOT_VALID) ?? false,
      },
      [TransitStatus.DISCREPANCIES as string]: {
        label: 'Discrepancies',
        checked: filters.status?.includes(TransitStatus.DISCREPANCIES) ?? false,
      },
      [TransitStatus.INCIDENT as string]: {
        label: 'Incident',
        checked: filters.status?.includes(TransitStatus.INCIDENT) ?? false,
      },
      [TransitStatus.RECOVERY as string]: {
        label: 'Recovery',
        checked: filters.status?.includes(TransitStatus.RECOVERY) ?? false,
      },
    };
  }, [filters.status]);

  const notificationsDropdownOptions = useMemo<ListFilterDropdownElements>(() => {
    return {
      [CustomsStateNotificationType.NOTIFICATION_PENDING]: {
        label: 'Notifications pending',
        checked: filters.filterByNotifications?.includes(CustomsStateNotificationType.NOTIFICATION_PENDING) ?? false,
      },
    };
  }, [filters.filterByNotifications]);

  const filterByNotificationsHandler = (notificationOption) => {
    const { id, checked } = notificationOption;

    if (!id) return;

    const current = filters.filterByNotifications || [];

    const updated = checked ? [...current, id] : current.filter((item) => item !== id);

    setNewFilter('filterByNotifications', updated);
  };

  const columns = useTransitsTableColumns({ transitView, pagination });

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const setNewFilter = (field: string, value: string | string[]) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...{ [field]: value },
    }) as TransitsCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ page: 1, size: pagination.size });
  };

  useEffect(() => {
    storeCriteria();
  }, [pagination, sorting, filters]);

  const handleTransitType = (value: TransitGroup) => {
    setTransitView(value);
    setNewFilter('group', value);
  };

  const searchTransits = (searchQuery: string) => {
    setNewFilter('searchQuery', searchQuery);
  };

  const toggleNewTransitModal = () => {
    transitModalManager.openCreateTransit(transitView);
  };

  const filterByStatus = (statusOption: ListFilterChangeEvent) => {
    const statusesOptionsCopy = cloneDeep(statusesOptions);
    if (!statusOption.id) {
      Object.keys(statusesOptionsCopy).forEach((option) => {
        statusesOptionsCopy[option].checked = statusOption.checked;
      });
    } else {
      statusesOptionsCopy[statusOption.id as TransitStatus].checked = statusOption.checked;
    }

    const selectedStatuses = Object.keys(statusesOptionsCopy)
      .filter((option) => statusesOptionsCopy[option].checked)
      .map((option) => option);

    setNewFilter('status', selectedStatuses?.length ? selectedStatuses : null);
  };

  if (!transitList) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Transits</ContentLayout.Title>
        <ContentLayout.Filters>
          <TransitType value={transitView} onChange={handleTransitType} />
          <Button data-pw="new-transit-btn" onClick={toggleNewTransitModal} variant="outline" size="md" h={55}>
            New transit
          </Button>
          <SearchBox
            handleSearch={searchTransits}
            value={filters?.searchQuery}
            placeholder="Seach by counter, name, LRN or MRN"
            data-pw="transit-search-input"
          />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="flex-end" mb="lg">
          <ListFilterDropdown title={'Notifications'} values={notificationsDropdownOptions} onChange={filterByNotificationsHandler} hideShowAll />
          <ListFilterDropdown title={'Status filter'} values={statusesOptions} onChange={filterByStatus} />
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={transitList || []}
          total={totalTransits}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
